import axios from 'axios';

// Use relative path so CRA proxy forwards /api/* to backend
// In production deployment, the proxy in setupProxy.js handles routing
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Business API
export const businessApi = {
  getAll: (params = {}) => axios.get(`${API}/businesses`, { params }),
  getById: (id) => axios.get(`${API}/businesses/${id}`),
  getMine: () => axios.get(`${API}/businesses/owner/me`, { headers: getAuthHeader() }),
  create: (formData) => axios.post(`${API}/businesses`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => axios.put(`${API}/businesses/${id}`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  getFeatured: () => axios.get(`${API}/businesses`, { params: { featured_only: true } }),
  getMinorityOwned: () => axios.get(`${API}/businesses`, { params: { minority_only: true } }),
  getAnalytics: (id) => axios.get(`${API}/businesses/${id}/analytics`, { headers: getAuthHeader() }),
};

// Event API
export const eventApi = {
  getAll: (params = {}) => axios.get(`${API}/events`, { params }),
  getById: (id) => axios.get(`${API}/events/${id}`),
  getMine: () => axios.get(`${API}/events/user/me`, { headers: getAuthHeader() }),
  create: (formData) => axios.post(`${API}/events`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  getFeatured: () => axios.get(`${API}/events`, { params: { featured_only: true } }),
};

// Payment API
export const paymentApi = {
  createBusinessCheckout: (productType, businessId) => 
    axios.post(`${API}/payments/checkout/business`, null, {
      params: { product_type: productType, business_id: businessId },
      headers: getAuthHeader()
    }),
  createEventCheckout: (productType, eventId) =>
    axios.post(`${API}/payments/checkout/event`, null, {
      params: { product_type: productType, event_id: eventId },
      headers: getAuthHeader()
    }),
  getStatus: (sessionId) =>
    axios.get(`${API}/payments/status/${sessionId}`, { headers: getAuthHeader() }),
  getHistory: () =>
    axios.get(`${API}/payments/history`, { headers: getAuthHeader() }),
};

// Admin API
export const adminApi = {
  getStats: () => axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
  getPending: () => axios.get(`${API}/admin/pending`, { headers: getAuthHeader() }),
  approveBusiness: (id) => axios.post(`${API}/admin/businesses/${id}/approve`, null, { headers: getAuthHeader() }),
  rejectBusiness: (id, reason) => axios.post(`${API}/admin/businesses/${id}/reject`, null, {
    params: { reason },
    headers: getAuthHeader()
  }),
  approveEvent: (id) => axios.post(`${API}/admin/events/${id}/approve`, null, { headers: getAuthHeader() }),
  rejectEvent: (id, reason) => axios.post(`${API}/admin/events/${id}/reject`, null, {
    params: { reason },
    headers: getAuthHeader()
  }),
  featureBusiness: (id, days) => axios.post(`${API}/admin/businesses/${id}/feature`, null, {
    params: { days },
    headers: getAuthHeader()
  }),
  featureEvent: (id, days) => axios.post(`${API}/admin/events/${id}/feature`, null, {
    params: { days },
    headers: getAuthHeader()
  }),
  togglePromotion: (active) => axios.post(`${API}/admin/settings/promotion`, null, {
    params: { active },
    headers: getAuthHeader()
  }),
  updateFreeFeaturedSlots: (payload) => axios.post(`${API}/admin/settings/free-featured-slots`, payload, {
    headers: getAuthHeader()
  }),
  getExpiringFeatured: (windowDays = 7) => axios.get(`${API}/admin/expiring-featured`, {
    params: { window_days: windowDays },
    headers: getAuthHeader()
  }),
  sendRenewalEmail: (businessId) => axios.post(`${API}/admin/businesses/${businessId}/send-renewal-email`, null, {
    headers: getAuthHeader()
  }),
  emailExpiringDigest: (windowDays = 7) => axios.post(`${API}/admin/email-expiring-digest`, null, {
    params: { window_days: windowDays },
    headers: getAuthHeader()
  }),
  getUsers: (userType) => axios.get(`${API}/admin/users`, {
    params: userType ? { user_type: userType } : {},
    headers: getAuthHeader()
  }),
  toggleUser: (id) => axios.post(`${API}/admin/users/${id}/toggle`, null, { headers: getAuthHeader() }),
  updateUserRole: (id, userType) => axios.put(`${API}/admin/users/${id}/role`, { user_type: userType }, { headers: getAuthHeader() }),
  generatePasswordReset: (id) => axios.post(`${API}/admin/users/${id}/password-reset`, null, { headers: getAuthHeader() }),
  sendWelcomeEmail: (id) => axios.post(`${API}/admin/users/${id}/send-welcome`, null, { headers: getAuthHeader() }),
  getEmailActivityStats: () => axios.get(`${API}/admin/email-activity/stats`, { headers: getAuthHeader() }),
  getEmailActivity: (params = {}) => axios.get(`${API}/admin/email-activity`, { params, headers: getAuthHeader() }),
  getReviews: (status) => axios.get(`${API}/admin/reviews`, { params: status ? { status } : {}, headers: getAuthHeader() }),
  approveReview: (id) => axios.post(`${API}/admin/reviews/${id}/approve`, null, { headers: getAuthHeader() }),
  rejectReview: (id) => axios.post(`${API}/admin/reviews/${id}/reject`, null, { headers: getAuthHeader() }),
  deleteReview: (id) => axios.delete(`${API}/admin/reviews/${id}`, { headers: getAuthHeader() }),
  getRedeployStatus: () => axios.get(`${API}/admin/redeploy-status`, { headers: getAuthHeader() }),
  getPayments: (params = {}) => axios.get(`${API}/admin/payments`, {
    params,
    headers: getAuthHeader()
  }),
  getPaymentsSummary: () => axios.get(`${API}/admin/payments/summary`, { headers: getAuthHeader() }),
  reconcilePendingPayments: () => axios.post(`${API}/admin/payments/reconcile-pending`, null, { headers: getAuthHeader() }),
  resendReceipt: (sessionId) => axios.post(`${API}/admin/payments/${sessionId}/resend-receipt`, null, { headers: getAuthHeader() }),
  createBusiness: (formData) => axios.post(`${API}/admin/businesses/create`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  createEvent: (formData) => axios.post(`${API}/admin/events/create`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  getAllBusinesses: (status) => axios.get(`${API}/admin/businesses`, {
    params: status ? { status } : {},
    headers: getAuthHeader()
  }),
  getAllEvents: (status) => axios.get(`${API}/admin/events`, {
    params: status ? { status } : {},
    headers: getAuthHeader()
  }),
  editBusiness: (id, formData) => axios.put(`${API}/admin/businesses/${id}`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  editEvent: (id, formData) => axios.put(`${API}/admin/events/${id}`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
  }),
  toggleFeaturedBusiness: (id) => axios.post(`${API}/admin/businesses/${id}/toggle-featured`, {}, { headers: getAuthHeader() }),
  toggleFeaturedEvent: (id) => axios.post(`${API}/admin/events/${id}/toggle-featured`, {}, { headers: getAuthHeader() }),
  deleteBusiness: (id) => axios.delete(`${API}/admin/businesses/${id}`, { headers: getAuthHeader() }),
  deleteEvent: (id) => axios.delete(`${API}/admin/events/${id}`, { headers: getAuthHeader() }),
};

// Categories API
export const categoriesApi = {
  getBusinessCategories: () => axios.get(`${API}/categories/business`),
  getBusinessSubcategories: (category) => axios.get(`${API}/categories/business/subcategories`, { params: category ? { category } : {} }),
  getEventCategories: () => axios.get(`${API}/categories/event`),
  getOwnershipTags: () => axios.get(`${API}/ownership-tags`),
};

// Auth API
export const authApi = {
  verifyEmail: (token) => axios.get(`${API}/auth/verify-email`, { params: { token } }),
  resendVerification: (email) => axios.post(`${API}/auth/resend-verification`, { email }),
};
